// Renders unit markers as voxel models with HP bars,
// damage flash, floating damage numbers, and death dissolve effects.
//
// P0.1-full: units are grouped by prefab-kind and rendered via VoxelUnitBatch
// (one InstancedMesh per prefab-kind) instead of one InstancedMesh per entity.
// See docs/superpowers/plans/2026-06-23-voxel-batching-p0_1-full.md.

import type { SceneNode } from '@aod/protocol';
import { playerColor } from '@aod/ui';
import { NAVAL_KINDS, getCreaturePrefab } from '@aod/ui/prefabs';
import type { CharacterPrefab } from '@aod/ui/prefabs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { merchantColour, merchantHouseOf, unitPrefabId } from '../sea/merchantModel.js';
import { DissolveSprite } from './DissolveSprite.js';
import { HEX_HEIGHT } from './HexGrid.js';
import { VoxelUnitBatch } from './VoxelUnitBatch.js';
import type { BatchedUnit } from './VoxelUnitBatch.js';
import { tileElevationAt } from './elevation.js';
import { crossingYaw, isRiverEdgeCrossing } from './shipCrossing.js';
import { unitTexturePath } from './spriteMapping.js';
import { toRenderableEngagement } from './toRenderableEngagement.js';
import type { EngagementVisualState } from './useCombatEngagements.js';

const WATER_BIOMES: ReadonlySet<string> = new Set(['ocean', 'coast', 'river']);
const WATER_SURFACE_LIFT = 0.02;
const MOVE_SETTLE_MS = 250;

// Module-level cache: prefabId → tallest voxel y. Avoids re-walking the parts
// array every render. Prefabs are immutable so this cache never invalidates.
const prefabHeightCache = new Map<string, number>();

function computePrefabHeight(prefab: CharacterPrefab): number {
  let maxY = 0;
  for (const part of prefab.parts) {
    for (const v of part.voxels) {
      if (v.y > maxY) maxY = v.y;
    }
  }
  return maxY;
}

function getPrefabHeight(prefabId: string): number {
  const cached = prefabHeightCache.get(prefabId);
  if (cached !== undefined) return cached;
  const prefab = getCreaturePrefab(prefabId);
  const h = prefab ? computePrefabHeight(prefab) : 16;
  prefabHeightCache.set(prefabId, h);
  return h;
}

interface UnitsProps {
  readonly units: ReadonlyArray<SceneNode>;
  readonly onClick?: (unit: SceneNode) => void;
  /** Hover in/out — null when the pointer leaves a unit sprite. */
  readonly onUnitHover?: (unit: SceneNode | null) => void;
  readonly selectedId?: string | null;
  readonly visibleCoords?: Set<string> | null;
  readonly exploredCoords?: Set<string>;
  readonly biomeMap?: Map<string, string>;
  /** Combat-engagement visual state keyed by unit id. Drives jitter + step-back. */
  readonly engagements?: ReadonlyMap<string, EngagementVisualState>;
  /** Current sim tick — used to compare against per-unit jitter windows. */
  readonly currentTick?: number;
}

interface DamageNumberState {
  readonly id: number;
  readonly damage: number;
  readonly x: number;
  readonly z: number;
  readonly y: number;
}

interface DyingUnit {
  readonly id: string;
  readonly node: SceneNode;
}

/** Ship mid-crossing: interpolate from the departed tile centre through the
 * shared edge (the drawn river channel) instead of snapping to the new one. */
interface ShipCrossing {
  readonly fromX: number;
  readonly fromZ: number;
  readonly startMs: number;
  readonly untilMs: number;
  readonly yaw: number;
}

let nextDmgId = 0;

export function VoxelUnits({
  units,
  onClick,
  onUnitHover,
  selectedId,
  visibleCoords,
  exploredCoords: _exploredCoords,
  biomeMap,
  engagements,
  currentTick = 0,
}: UnitsProps) {
  const [damageNumbers, setDamageNumbers] = useState<ReadonlyArray<DamageNumberState>>([]);
  const [dyingUnits, setDyingUnits] = useState<ReadonlyArray<DyingUnit>>([]);
  // Per-unit flash-start timestamps (performance.now() ms). Cleared when the
  // unit disappears. Triggers a re-render on damage so VoxelUnitBatch picks
  // up the new flash value.
  const [flashStarts, setFlashStarts] = useState<Map<string, number>>(new Map());

  const unitHpRef = useRef<Map<string, number>>(new Map());
  const prevUnitIdsRef = useRef<Set<string>>(new Set());
  const prevUnitMapRef = useRef<Map<string, SceneNode>>(new Map());
  const prevPosRef = useRef<Map<string, [number, number]>>(new Map());
  const movingUntilRef = useRef<Map<string, number>>(new Map());
  const crossingRef = useRef<Map<string, ShipCrossing>>(new Map());

  // Filter units by fog-of-war visibility.
  const visibleUnits = useMemo(() => {
    if (!visibleCoords) return units;
    return units.filter((u) => {
      const q = u.props?.q as number | undefined;
      const r = u.props?.r as number | undefined;
      return q !== undefined && r !== undefined && visibleCoords.has(`${q},${r}`);
    });
  }, [units, visibleCoords]);

  // HP damage tracking → floating damage numbers + flash-start updates.
  useEffect(() => {
    const now = performance.now();
    const newNumbers: DamageNumberState[] = [];
    const flashUpdates = new Map<string, number>();
    for (const unit of visibleUnits) {
      const id = unit.id;
      const hp = (unit.props?.hp as number) ?? 10;
      const prev = unitHpRef.current.get(id);
      if (prev !== undefined && hp < prev) {
        const [x, , z] = unit.transform.position;
        newNumbers.push({
          id: nextDmgId++,
          damage: prev - hp,
          x,
          z,
          y: HEX_HEIGHT / 2 + 0.8,
        });
        flashUpdates.set(id, now);
      }
      unitHpRef.current.set(id, hp);
    }
    if (newNumbers.length > 0) {
      setDamageNumbers((prev) => [...prev, ...newNumbers]);
      for (const n of newNumbers) {
        setTimeout(() => {
          setDamageNumbers((cur) => cur.filter((d) => d.id !== n.id));
        }, 1000);
      }
    }
    if (flashUpdates.size > 0) {
      setFlashStarts((prev) => new Map([...prev, ...flashUpdates]));
    }
  }, [visibleUnits]);

  // Detect units that disappeared from the full unit list (truly died, not just fogged).
  useEffect(() => {
    const currentIds = new Set(units.map((u) => u.id));
    const prevIds = prevUnitIdsRef.current;
    const prevMap = prevUnitMapRef.current;

    const newlyDead: DyingUnit[] = [];
    for (const prevId of prevIds) {
      if (!currentIds.has(prevId)) {
        const prevNode = prevMap.get(prevId);
        if (prevNode) {
          newlyDead.push({ id: prevId, node: prevNode });
        }
        unitHpRef.current.delete(prevId);
        prevPosRef.current.delete(prevId);
        movingUntilRef.current.delete(prevId);
        crossingRef.current.delete(prevId);
      }
    }

    prevUnitIdsRef.current = currentIds;
    prevUnitMapRef.current = new Map(units.map((u) => [u.id, u] as const));

    if (newlyDead.length > 0) {
      setDyingUnits((prev) => [...prev, ...newlyDead]);
      setFlashStarts((prev) => {
        const next = new Map(prev);
        for (const d of newlyDead) next.delete(d.id);
        return next;
      });
    }
  }, [units]);

  // Group visible units by prefab-kind and build BatchedUnit payloads.
  const batches = useMemo<Array<{ prefabId: string; entries: BatchedUnit[] }>>(() => {
    const now = performance.now();
    const groups = new Map<string, BatchedUnit[]>();

    for (const unit of visibleUnits) {
      const id = unit.id;
      const [ux, , uz] = unit.transform.position;
      const hp = (unit.props?.hp as number) ?? 10;
      const hpMax = (unit.props?.hpMax as number) ?? 10;
      const kind = (unit.props?.kind as string) ?? 'warrior';
      const ownerId = unit.props?.ownerId as number | undefined;
      const q = unit.props?.q as number | undefined;
      const r = unit.props?.r as number | undefined;
      // A foreign house's vessel: its own hull, painted in the house's colour.
      const house = merchantHouseOf(ownerId, unit.props?.seaHouse as number | undefined);
      const prefabId = unitPrefabId(kind, ownerId, unit.props?.seaHouse as number | undefined);

      // Update prev-pos + moving-settle timestamp.
      const prevPos = prevPosRef.current.get(id);
      if (prevPos) {
        const dx = ux - prevPos[0];
        const dz = uz - prevPos[1];
        const moved = Math.abs(dx) + Math.abs(dz) > 0.001;
        if (moved) {
          movingUntilRef.current.set(id, now + MOVE_SETTLE_MS);
          // River-going ships hop tile-centre to tile-centre across a river
          // edge; interpolate so the sprite rides the drawn channel.
          if (isRiverEdgeCrossing(kind, dx, dz)) {
            crossingRef.current.set(id, {
              fromX: prevPos[0],
              fromZ: prevPos[1],
              startMs: now,
              untilMs: now + MOVE_SETTLE_MS,
              yaw: crossingYaw(dx, dz),
            });
          } else {
            crossingRef.current.delete(id);
          }
        }
      }
      prevPosRef.current.set(id, [ux, uz]);
      const movingUntil = movingUntilRef.current.get(id);
      const moving = movingUntil !== undefined && now < movingUntil;
      if (movingUntil !== undefined && !moving) {
        movingUntilRef.current.delete(id);
        crossingRef.current.delete(id);
      }

      // Naval water-elevation snap.
      const isNaval = NAVAL_KINDS.has(kind);
      const biome =
        biomeMap && q !== undefined && r !== undefined ? biomeMap.get(`${q},${r}`) : undefined;
      const onWater = biome !== undefined && WATER_BIOMES.has(biome);
      const baseY =
        isNaval && onWater && q !== undefined && r !== undefined && biomeMap
          ? tileElevationAt(q, r, biomeMap) + WATER_SURFACE_LIFT
          : biomeMap && q !== undefined && r !== undefined && !onWater
            ? // Stand on the tile (plateau + any hill/mountain peak).
              tileElevationAt(q, r, biomeMap) + 0.02
            : HEX_HEIGHT / 2 + 0.05;

      const crossingState = crossingRef.current.get(id);
      const crossing =
        moving && crossingState && now < crossingState.untilMs ? crossingState : null;

      const entry: BatchedUnit = {
        id,
        ownerId,
        // A house paints its own hull; every other unit wears its owner's colour.
        factionColor: house !== null ? merchantColour(house) : undefined,
        baseX: ux,
        baseZ: uz,
        baseY,
        hp,
        hpMax,
        selected: selectedId === id,
        moving,
        crossing,
        prefabHeight: getPrefabHeight(prefabId),
        flashStart: flashStarts.get(id) ?? 0,
        ...toRenderableEngagement(engagements?.get(id)),
      };
      const arr = groups.get(prefabId);
      if (arr) arr.push(entry);
      else groups.set(prefabId, [entry]);
    }

    return Array.from(groups.entries()).map(([prefabId, entries]) => ({
      prefabId,
      entries,
    }));
  }, [visibleUnits, selectedId, biomeMap, flashStarts, engagements]);

  const removeDying = useCallback((id: string) => {
    setDyingUnits((prev) => prev.filter((d) => d.id !== id));
  }, []);

  return (
    <>
      {batches.map(({ prefabId, entries }) => (
        <VoxelUnitBatch
          key={`batch-${prefabId}`}
          prefabId={prefabId}
          units={entries}
          currentTick={currentTick}
          onClick={(unitId) => {
            const node = units.find((u) => u.id === unitId);
            if (node) onClick?.(node);
          }}
          onHover={(unitId) => {
            if (unitId === null) {
              onUnitHover?.(null);
              return;
            }
            const node = units.find((u) => u.id === unitId);
            onUnitHover?.(node ?? null);
          }}
        />
      ))}
      {dyingUnits.map((d) => {
        const [dx, , dz] = d.node.transform.position;
        const kind = (d.node.props?.kind as string) ?? 'warrior';
        const ownerId = d.node.props?.ownerId as number | undefined;
        const house = merchantHouseOf(ownerId, d.node.props?.seaHouse as number | undefined);
        const factionColor = house !== null ? merchantColour(house) : playerColor(ownerId);
        return (
          <DissolveSprite
            key={`dying-${d.id}`}
            textureUrl={unitTexturePath(kind, ownerId)}
            position={[dx, 0, dz]}
            color={factionColor}
            onComplete={() => removeDying(d.id)}
          />
        );
      })}
      {damageNumbers.map((dn) => (
        <FloatingDamage key={`dmg-${dn.id}`} {...dn} />
      ))}
    </>
  );
}

function FloatingDamage({ damage, x, z, y }: DamageNumberState) {
  const [offset, setOffset] = useState(0);
  const [opacity, setOpacity] = useState(1);
  useEffect(() => {
    const start = performance.now();
    let raf: number;
    const animate = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / 1000, 1);
      setOffset(t * 0.6);
      setOpacity(1 - t);
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  const text = `-${damage}`;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 48;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, 64, 48);
  ctx.font = 'bold 28px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = `rgba(255, 60, 60, ${opacity})`;
  ctx.fillText(text, 32, 24);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(0.8, 0.6, 1);

  return (
    <sprite position={[x, y + offset, z]} scale={[0.8, 0.6, 1]}>
      <spriteMaterial map={texture} transparent opacity={opacity} depthTest={false} />
    </sprite>
  );
}
